import { Dialog, DialogContent, DialogTitle, Card, Tabs, Tab, Box, Typography, SxProps, Theme } from '@mui/material';
import { useState } from 'react';
import { TrendingUp, Calendar, Clock } from 'lucide-react';
import { ClockDisplay } from './clock';
import TomatopAnimationBox from './TomatopAnimation';
import { useHeatMapColors } from '../../../hooks/useHeatMapColors';
import { useTimerStore } from '../../../stores/TimerStore';
import { useTaskStore } from '../../../stores/TaskStore';
import { useLoginStatusStore } from '../../../stores/LoginStatusStore';
import { useTranslations } from 'next-intl';
import D3HeatMap from './D3HeatMap';
import CustomMuiScrollBar from '@/components/ui/ScrollBar';

interface WeeklyStatsPanelProps {
  open: boolean;
  onClose: () => void;
}

interface LoginRequiredWrapperProps {
  isLoggedIn: boolean;
  children: React.ReactNode;
  className?: string;
  title?: string;
}

const LoginRequiredWrapper = ({ isLoggedIn, children, className = '', title }: LoginRequiredWrapperProps) => {
  const t = useTranslations('WeeklyStatsPanel');

  if (isLoggedIn) {
    return <>{children}</>;
  }

  return (
    <div className={`relative ${className}`}>
      <div style={{ filter: 'blur(4px)' }}>
        {children}
      </div>
      <div
        className="absolute inset-0 flex items-center justify-center rounded-lg"
        style={{
          backgroundColor: 'var(--color-card)',
          opacity: 0.95,
          backdropFilter: 'blur(4px)',
        }}
      >
        <div className="text-center">
          {title && (
            <Typography
              className="text-sm font-semibold mb-1"
              style={{ color: 'var(--color-foreground)' }}
            >
              {title}
            </Typography>
          )}
          <Typography
            className="text-sm font-medium"
            style={{ color: 'var(--color-foreground)' }}
          >
            {t('pleaseLoginToView')}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const LocalCardSx: SxProps<Theme> = {
  border: '1px solid var(--color-border)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  borderRadius: '10px',
  backgroundColor: 'var(--color-statistics-panel-bg)',
  transition: 'background-color 0.3s ease',
}

export const WeeklyStatsPanel = ({
  open,
  onClose,
}: WeeklyStatsPanelProps) => {
  const t = useTranslations('WeeklyStatsPanel');
  const [activeTab, setActiveTab] = useState(0);
  const { colors, panelColors } = useHeatMapColors();
  const { isLoggedIn } = useLoginStatusStore();
  const pomodoroHistory = useTimerStore(state => state.pomodoroHistory);
  const completedTasks = useTaskStore(state => state.completedTasks);
  const taskHistory = useTaskStore(state => state.taskHistory);

  const weeklyData = {
    pomodoroCount: pomodoroHistory.reduce((acc, cur) => acc + cur.count, 0),
    taskCount: taskHistory.reduce((acc, cur) => acc + cur.count, 0),
    dailyData: pomodoroHistory.map(item => ({
      date: item.date,
      day: new Date(item.date).getDay().toString(),
      pomodoroCount: item.count,
      taskCount: completedTasks.filter(task => task.completedAt && new Date(task.completedAt).toISOString().slice(0, 10) === item.date).length,
    })),
    bestWorkDay: t('noData'),
    commonTags: [],
    mostProductiveTime: '0:00 - 24:00',
    dailyAverage: 0,
    growthRate: 0
  };


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderHeatMap = () => {
    // 根据当前标签页选择不同的数据源
    const heatMapData = activeTab === 0 ? pomodoroHistory : taskHistory;

    return <>
      <div className="relative p-3 sm:p-4 rounded-xl border" style={{
        backgroundColor: 'var(--color-statistics-panel-bg)',
        borderColor: 'var(--color-border)',
        transition: 'background-color 0.3s ease',
      }}>
        <div className="mb-3 flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
          <h3 className="text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>{t('activityHeatmap')}</h3>
          <div className="flex items-center justify-center sm:justify-end space-x-2 text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            <Typography className='text-xs sm:text-sm'>{t('less')}</Typography>
            <div className="flex space-x-1">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
                  style={{
                    backgroundColor: panelColors[level as keyof typeof panelColors] || colors.base
                  }}
                />
              ))}
            </div>
            <Typography className='text-xs sm:text-sm'>{t('more')}</Typography>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[320px]">
            <D3HeatMap
              data={heatMapData}
              width={800}
              cellSize={12}
              cellGap={2}
              panelColors={panelColors}
              baseColor={colors.base}
              tooltipContent={(data) => (
                <div style={{ textAlign: 'center' }}>
                  <Typography className='text-xs' style={{ fontWeight: 500 }}>
                    {activeTab === 0
                      ? t('pomodoros', { count: data.count })
                      : t('tasks', { count: data.count })
                    }
                  </Typography>
                  <Typography className='text-xs' style={{ opacity: 0.75 }}>
                    {data.dateStr}
                  </Typography>
                </div>
              )}
              onCellHover={() => {

              }}
              onCellClick={() => {

              }}
            />
          </div>
        </div>

        <div className="mt-3 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-1 sm:space-y-0 text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
          <Typography className='text-xs sm:text-sm text-center sm:text-left'>{t('pastYearActivity')}</Typography>
          <Typography className='text-xs sm:text-sm text-center sm:text-right'>
            {activeTab === 0
              ? t('pomodoros', { count: heatMapData.reduce((sum, item) => sum + (item.count || 0), 0) })
              : t('tasks', { count: heatMapData.reduce((sum, item) => sum + (item.count || 0), 0) })
            }
          </Typography>
        </div>
      </div>
    </>
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            margin: { xs: '16px', sm: '32px' },
            maxHeight: { xs: '90vh', sm: '85vh' },
            width: { xs: 'calc(100% - 32px)', sm: 'auto' },
            backgroundColor: 'var(--color-card)',
            color: 'var(--color-foreground)',
            transition: 'background-color 0.3s ease, color 0.3s ease',
          }
        }
      }}
    >
      <DialogTitle className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 color-primary" />
            <Typography className="text-xl">{t('dataStatistics')}</Typography>
          </div>
        </div>
      </DialogTitle>

      <DialogContent
        className="p-0"
        sx={{
          maxHeight: { xs: '70vh', sm: '70vh' },
          overflowY: 'auto',
          padding: { xs: 0, sm: 0 },
          ...CustomMuiScrollBar()
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'var(--color-border)' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className="px-4 sm:px-6"
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minWidth: { xs: 'auto', sm: 160 },
                padding: { xs: '8px 12px', sm: '12px 16px' },
                color: 'var(--color-muted-foreground)',
                transition: 'color 0.3s ease',
                '&.Mui-selected': {
                  color: 'var(--color-primary)',
                },
                '&:hover': {
                  color: 'var(--color-primary-hover)',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'var(--color-primary)',
                transition: 'background-color 0.3s ease',
              }
            }}
          >
            <Tab label={t('pomodoroStatistics')} />
            <Tab label={t('taskStatistics')} />
          </Tabs>
        </Box>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <TomatopAnimationBox boxCount={activeTab === 0 ? weeklyData.pomodoroCount : weeklyData.taskCount}>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: 'var(--color-foreground)' }}>
                  {activeTab === 0 ? weeklyData.pomodoroCount : weeklyData.taskCount}
                </div>
                <Typography className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                  {t('total')}
                </Typography>
              </div>
            </TomatopAnimationBox>

            <TomatopAnimationBox boxCount={activeTab === 0 ? weeklyData.pomodoroCount : weeklyData.taskCount}>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: 'var(--color-foreground)' }}>
                  {activeTab === 0 ? weeklyData.pomodoroCount : weeklyData.taskCount}
                </div>
                <Typography className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                  {t('dailyAverage')}
                </Typography>
              </div>
            </TomatopAnimationBox>
            <TomatopAnimationBox boxCount={weeklyData.growthRate}>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: 'var(--color-foreground)' }}>
                  {weeklyData.growthRate >= 0 ? '+' : ''}{weeklyData.growthRate}
                </div>
                <Typography className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>{t('monthlyGrowth')}</Typography>
              </div>
            </TomatopAnimationBox>
          </div>

          <div className='space-y-0'>
            <Typography className="mb-2 text-sm">
              {t('heatmapDescription')}
            </Typography>
            <Card sx={{
              border: 'none',
              boxShadow: 'none',
              marginBottom: 0,
              backgroundColor: 'transparent',
            }}>
              {renderHeatMap()}
            </Card>
          </div>

          {activeTab === 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <LoginRequiredWrapper isLoggedIn={isLoggedIn} title={t('mostActiveDay')}>
                <Card className="p-3 sm:p-4" sx={LocalCardSx}>
                  <div className="space-y-2">
                    <Typography className="text-sm font-medium flex items-center" style={{ color: 'var(--color-foreground)' }}>
                      <Calendar className="h-4 w-4 mr-2" />
                      {t('mostActiveDay')}
                    </Typography>
                    <div className="text-base sm:text-lg font-semibold" style={{ color: 'var(--color-foreground)' }}>
                      {weeklyData.bestWorkDay}
                    </div>
                    <Typography className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                      {t('higherThanAverage', { average: weeklyData.dailyAverage })}
                    </Typography>
                  </div>
                </Card>
              </LoginRequiredWrapper>

              <LoginRequiredWrapper isLoggedIn={isLoggedIn} title={t('mostActiveTime')}>
                <Card className="p-3 sm:p-4" sx={LocalCardSx}>
                  <div className="space-y-2">
                    <Typography className="text-sm font-medium flex items-center" style={{ color: 'var(--color-foreground)' }}>
                      <Clock className="h-4 w-4 mr-2" />
                      {t('mostActiveTime')}
                    </Typography>
                    <Typography className="text-sm sm:text-lg font-semibold" style={{ color: 'var(--color-foreground)' }}>
                      {weeklyData.mostProductiveTime}
                    </Typography>

                    <div className="flex justify-center mt-3 sm:mt-4">
                      <ClockDisplay
                        startHour={9}
                        startMinute={0}
                        endHour={17}
                        endMinute={30}
                        size={80}
                        highlightColor="#3b82f6"
                      />
                    </div>
                  </div>
                </Card>
              </LoginRequiredWrapper>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              <LoginRequiredWrapper isLoggedIn={isLoggedIn} title={t('commonTags')}>
                <Card className="p-3 sm:p-4" sx={LocalCardSx}>
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>{t('commonTags')}</h4>
                    <div className="text-base sm:text-lg font-semibold" style={{ color: 'var(--color-foreground)' }}>
                      {weeklyData.commonTags.length > 0 ? weeklyData.commonTags.join(', ') : t('noData')}
                    </div>
                    <Typography className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                      {t('tagsDescription')}
                    </Typography>
                  </div>
                </Card>
              </LoginRequiredWrapper>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
